import React, { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../services/api";

const DEFAULT_CENTER = [18.1514, 74.5815]; // Baramati, Maharashtra

const centerPinIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%232563eb" width="36px" height="36px"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -32],
});

const createDriverIcon = (status) => {
  const color = status === "FREE" ? "%2316a34a" : "%23dc2626";
  return new L.Icon({
    iconUrl: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="32px" height="32px"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -28],
  });
};

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapPanController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.panTo(center);
    }
  }, [center, map]);
  return null;
}

// Separate from MapPanController: a flyTo with zoom, specifically for search.
function MapFlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, 13, { duration: 1.2 });
  }, [target, map]);
  return null;
}

const formatLastUpdated = (iso) => {
  if (!iso) return "Never";
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  return new Date(iso).toLocaleString();
};

export default function RadiusBookingMap() {
  const [activeBookings, setActiveBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [radiusDrivers, setRadiusDrivers] = useState([]);

  const [radiusKm, setRadiusKm] = useState(5);
  const [customCenter, setCustomCenter] = useState(null);
  const [flyToTarget, setFlyToTarget] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);

  const fetchActiveBookings = useCallback(async () => {
    try {
      const res = await api.get("/map/active-bookings");
      if (res.data.success) {
        setActiveBookings(res.data.bookings || []);
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to load active bookings" });
    }
  }, []);

  const fetchDriversInRadius = useCallback(async (lat, lng, radius, vehicleType) => {
    try {
      setLoading(true);
      const params = { latitude: lat, longitude: lng, radiusKm: radius };
      if (vehicleType) params.vehicleType = vehicleType;

      const res = await api.get("/map/drivers-in-radius", { params });
      if (res.data.success) {
        setRadiusDrivers(res.data.drivers || []);
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to load drivers inside radius" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveBookings();
  }, [fetchActiveBookings]);

  const handleBookingSelect = (e) => {
    const bId = e.target.value;
    setSelectedBookingId(bId);

    const booking = activeBookings.find((b) => b._id === bId);
    if (booking && booking.pickupCoordinates) {
      setSelectedBooking(booking);
      const lat = booking.pickupCoordinates.latitude;
      const lng = booking.pickupCoordinates.longitude;
      const initialRadius = booking.configuredRadiusKm || 5;

      setRadiusKm(initialRadius);
      setCustomCenter([lat, lng]);
      fetchDriversInRadius(lat, lng, initialRadius, booking.vehicleType);
    }
  };

  const handleMapClick = (lat, lng) => {
    setSelectedBookingId("");
    setSelectedBooking(null);
    setCustomCenter([lat, lng]);

    fetchDriversInRadius(lat, lng, radiusKm);
  };

  const handleRadiusChange = (newRadius) => {
    setRadiusKm(newRadius);
    if (customCenter) {
      fetchDriversInRadius(
        customCenter[0],
        customCenter[1],
        newRadius,
        selectedBooking?.vehicleType
      );
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    try {
      setSearching(true);
      const res = await api.get("/map/search-location", { params: { query: searchQuery.trim() } });
      if (res.data.success) {
        setSelectedBookingId("");
        setSelectedBooking(null);
        const lat = res.data.latitude;
        const lng = res.data.longitude;
        setCustomCenter([lat, lng]);
        setFlyToTarget([lat, lng]);
        fetchDriversInRadius(lat, lng, radiusKm);
      } else {
        setMessage({ type: "error", text: "Location not found" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Search failed" });
    } finally {
      setSearching(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <h1 style={styles.title}>Booking Radius Map</h1>
        <p style={styles.subtitle}>
          Select a booking or click anywhere on the map to inspect drivers within radius
        </p>
      </div>

      {/* SEARCH BAR */}
      <form onSubmit={handleSearch} style={styles.searchBar}>
        <input
          type="text"
          placeholder="Search a place to focus the map (e.g. Pune, Baramati)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <button type="submit" style={styles.searchBtn} disabled={searching}>
          {searching ? "Searching..." : "Search"}
        </button>
      </form>

      {message && (
        <div
          style={{
            ...styles.banner,
            background: message.type === "success" ? "#dcfce7" : "#fee2e2",
            color: message.type === "success" ? "#166534" : "#991b1b",
          }}
        >
          {message.text}
        </div>
      )}

      {/* CONTROLS BAR */}
      <div style={styles.controlsCard}>
        <div style={styles.controlGroup}>
          <label style={styles.label}>Select Active Booking:</label>
          <select
            style={styles.select}
            value={selectedBookingId}
            onChange={handleBookingSelect}
          >
            <option value="">-- Custom Map Point (Click Anywhere) --</option>
            {activeBookings.map((b) => (
              <option key={b._id} value={b._id}>
                {b._id.slice(-6)} | {b.vehicleType?.toUpperCase()} | {b.status} | {b.pickupLocation}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.controlGroup}>
          <label style={styles.label}>Radius: {radiusKm} KM</label>
          <input
            type="range"
            min="1"
            max="25"
            value={radiusKm}
            onChange={(e) => handleRadiusChange(parseFloat(e.target.value))}
            style={styles.slider}
          />
        </div>

        <button
          style={styles.refreshBtn}
          onClick={() => {
            if (customCenter) {
              fetchDriversInRadius(
                customCenter[0],
                customCenter[1],
                radiusKm,
                selectedBooking?.vehicleType
              );
            }
          }}
        >
          {loading ? "Searching..." : "Refresh Radius"}
        </button>
      </div>

      {/* MAP WRAPPER */}
      <div style={styles.mapWrapper}>
        <MapContainer
          center={customCenter || DEFAULT_CENTER}
          zoom={12}
          style={{ width: "100%", height: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapClickHandler onMapClick={handleMapClick} />
          {customCenter && <MapPanController center={customCenter} />}
          <MapFlyTo target={flyToTarget} />

          {/* CENTER PIN */}
          {customCenter && (
            <Marker position={customCenter} icon={centerPinIcon}>
              <Popup>
                <div style={styles.infoWindow}>
                  <strong>Search Center Point</strong>
                  <br />
                  Radius: {radiusKm} KM
                </div>
              </Popup>
            </Marker>
          )}

          {/* RADIUS CIRCLE (Leaflet requires meters) */}
          {customCenter && (
            <Circle
              center={customCenter}
              radius={radiusKm * 1000}
              pathOptions={{
                fillColor: "#3b82f6",
                fillOpacity: 0.15,
                color: "#2563eb",
                weight: 2,
              }}
            />
          )}

          {/* RADIUS DRIVERS */}
          {radiusDrivers.map((driver) => {
            if (!driver.coordinates?.latitude || !driver.coordinates?.longitude) return null;

            return (
              <Marker
                key={driver._id}
                position={[driver.coordinates.latitude, driver.coordinates.longitude]}
                icon={createDriverIcon(driver.status)}
              >
                <Popup>
                  <div style={styles.infoWindow}>
                    <h4 style={styles.infoName}>{driver.name}</h4>
                    <p style={styles.infoText}>📞 {driver.phone}</p>
                    <p style={styles.infoText}>🚗 Vehicle: {driver.vehicleType}</p>

                    <div style={styles.badgeContainer}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          background: driver.status === "FREE" ? "#dcfce7" : "#fee2e2",
                          color: driver.status === "FREE" ? "#15803d" : "#b91c1c",
                        }}
                      >
                        {driver.status}
                      </span>
                      <span
                        style={{
                          ...styles.statusBadge,
                          marginLeft: "6px",
                          background: driver.isOnline ? "#dbeafe" : "#f1f5f9",
                          color: driver.isOnline ? "#1d4ed8" : "#64748b",
                        }}
                      >
                        {driver.isOnline ? "Online" : "Offline"}
                      </span>
                    </div>

                    <p style={styles.infoText}>🕒 Updated: {formatLastUpdated(driver.lastLocationUpdate)}</p>

                    {driver.status === "BUSY" && driver.currentBooking && (
                      <div style={styles.bookingBox}>
                        <div style={styles.bookingTitle}>Active Assignment</div>
                        <div><strong>Booking ID:</strong> {driver.currentBooking.bookingId}</div>
                        <div><strong>Status:</strong> {driver.currentBooking.bookingStatus}</div>
                        <div><strong>Pickup:</strong> {driver.currentBooking.pickupLocation}</div>
                        <div><strong>Drop:</strong> {driver.currentBooking.dropLocation}</div>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

/* ================= RESPONSIVE STYLES ================= */
const styles = {
  page: {
    padding: "clamp(12px, 3vw, 24px)",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Inter', sans-serif",
    boxSizing: "border-box",
  },
  header: {
    marginBottom: "16px",
  },
  title: {
    margin: 0,
    fontSize: "clamp(20px, 4vw, 26px)",
    fontWeight: "800",
    color: "#0f172a",
  },
  subtitle: {
    margin: "4px 0 0 0",
    color: "#64748b",
    fontSize: "clamp(12px, 2.5vw, 14px)",
  },
  searchBar: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
  },
  searchInput: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "13px",
    outline: "none",
  },
  searchBtn: {
    background: "#16a34a",
    color: "#ffffff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
  },
  controlsCard: {
    background: "#ffffff",
    padding: "16px",
    borderRadius: "12px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  controlGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: "1 1 220px",
    width: "100%",
  },
  label: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#475569",
  },
  select: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "13px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  slider: {
    width: "100%",
  },
  refreshBtn: {
    background: "#2563eb",
    color: "#ffffff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
    flex: "0 0 auto",
    alignSelf: "flex-end",
    minHeight: "40px",
  },
  banner: {
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    marginBottom: "12px",
  },
  mapWrapper: {
    height: "calc(100vh - 320px)",
    minHeight: "380px",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  },
  infoWindow: {
    padding: "4px",
    maxWidth: "220px",
    fontFamily: "'Inter', sans-serif",
  },
  infoName: {
    margin: "0 0 6px 0",
    fontSize: "14px",
    fontWeight: "700",
    color: "#0f172a",
  },
  infoText: {
    margin: "2px 0",
    fontSize: "12px",
    color: "#334155",
  },
  badgeContainer: {
    marginTop: "6px",
    marginBottom: "8px",
  },
  statusBadge: {
    padding: "3px 8px",
    borderRadius: "12px",
    fontSize: "10px",
    fontWeight: "800",
  },
  bookingBox: {
    marginTop: "8px",
    padding: "8px",
    background: "#f1f5f9",
    borderRadius: "6px",
    fontSize: "11px",
    color: "#1e293b",
    lineHeight: "1.4",
    wordBreak: "break-word",
  },
  bookingTitle: {
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "4px",
    borderBottom: "1px solid #cbd5e1",
    paddingBottom: "2px",
  },
};